import { useState, useEffect } from 'react';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { Users, DollarSign, Gamepad2, TrendingUp, Clock, ArrowDownCircle, ArrowUpCircle, Wallet, Activity, RefreshCw, CreditCard, Download } from 'lucide-react';

export default function TenantDashboardPage({ onNavigate }) {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchDashboard(); }, []);

  const fetchDashboard = async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/admin/tenant-dashboard');
      setData(res.data);
    } catch {} finally { setLoading(false); }
  };

  const exportCSV = async (type) => {
    try {
      const res = await api.get(`/api/admin/export/${type}`, { responseType: 'blob' });
      const disposition = res.headers['content-disposition'] || '';
      const match = disposition.match(/filename=([^;]+)/i);
      const filename = (match?.[1] || `${type}.csv`).replace(/"/g, '');
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {}
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="spinner" /></div>;
  if (!data) return <div className="text-center py-12 text-gray-500">No data available</div>;

  const s = data.stats || {};
  const w = data.wallets || {};
  const d = data.deposits || {};
  const wi = data.withdrawals || {};
  const g = data.games || {};
  const fmtUSD = (v) => `$${(Number(v) || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
  const fmtFP = (v) => `${(Number(v) || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })} FP`;

  return (
    <div className="space-y-6" data-testid="tenant-dashboard">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">{data.tenant?.name || 'Dashboard'}</h2>
          <p className="text-sm text-gray-400">Welcome, {user?.full_name || user?.username}</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative group">
            <button className="p-2 rounded-lg hover:bg-white/5 text-gray-400 transition" data-testid="export-menu-btn">
              <Download className="w-5 h-5" />
            </button>
            <div className="absolute right-0 top-10 w-48 bg-[#1a1f2e] border border-white/10 rounded-xl shadow-xl hidden group-hover:block z-50">
              <button onClick={() => exportCSV('players')} className="w-full text-left px-4 py-2.5 text-sm text-gray-300 hover:bg-white/5 rounded-t-xl" data-testid="export-players">Export Players CSV</button>
              <button onClick={() => exportCSV('transactions')} className="w-full text-left px-4 py-2.5 text-sm text-gray-300 hover:bg-white/5" data-testid="export-transactions">Export Transactions CSV</button>
              <button onClick={() => exportCSV('game-transactions')} className="w-full text-left px-4 py-2.5 text-sm text-gray-300 hover:bg-white/5 rounded-b-xl" data-testid="export-game-txns">Export Game Credits CSV</button>
            </div>
          </div>
          <button onClick={fetchDashboard} className="p-2 rounded-lg hover:bg-white/5 text-gray-400 transition" data-testid="refresh-tenant-dashboard">
            <RefreshCw className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* KPI Cards — clickable to navigate */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KPI icon={Users} label="Total Players" value={s.total_players || 0} sub={`+${s.new_players_24h || 0} today`} color="#3B82F6" onClick={() => onNavigate?.('players')} />
        <KPI icon={ArrowDownCircle} label="Pending Deposits" value={s.pending_deposits || 0} color="#10B981" urgent={s.pending_deposits > 0} onClick={() => onNavigate?.('deposit-requests')} />
        <KPI icon={ArrowUpCircle} label="Pending Withdrawals" value={s.pending_withdrawals || 0} color="#EF4444" urgent={s.pending_withdrawals > 0} onClick={() => onNavigate?.('withdrawal-requests')} />
        <KPI icon={Gamepad2} label="Game Accounts" value={s.game_accounts || 0} color="#D4AF37" />
      </div>

      {/* Wallets + Deposits/Withdrawals */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Wallet Summary */}
        <div className="bg-[#131822] rounded-2xl border border-white/10 p-5">
          <h3 className="text-sm font-semibold text-gray-400 mb-4 flex items-center gap-2"><Wallet className="w-4 h-4 text-[#D4AF37]" /> Player Wallets</h3>
          <div className="space-y-3">
            <WalletRow label="Total Cash" value={`${fmtUSD(w.total_cash)}`} color="#22c55e" />
            <WalletRow label="Play Credits" value={`${fmtFP(w.total_play_credits)}`} color="#3B82F6" />
            <WalletRow label="Pending" value={`${fmtUSD(w.total_pending)}`} color="#eab308" />
            <div className="pt-2 border-t border-white/5">
              <WalletRow label="Combined Cash" value={`${fmtUSD(w.total_cash || 0)}`} color="#D4AF37" bold />
            </div>
          </div>
        </div>

        {/* Deposits */}
        <div className="bg-[#131822] rounded-2xl border border-white/10 p-5 cursor-pointer hover:border-green-500/20 transition" onClick={() => onNavigate?.('deposit-requests')}>
          <h3 className="text-sm font-semibold text-gray-400 mb-4 flex items-center gap-2"><ArrowDownCircle className="w-4 h-4 text-green-400" /> Deposits</h3>
          <div className="grid grid-cols-2 gap-3">
            <MiniStat label="Total" value={`${fmtUSD(d.total_amount)}`} sub={`${d.total_count || 0} txns`} color="#22c55e" />
            <MiniStat label="Approved" value={`${fmtUSD(d.approved_amount)}`} sub={`${d.approved_count || 0} txns`} color="#16a34a" />
          </div>
        </div>

        {/* Withdrawals */}
        <div className="bg-[#131822] rounded-2xl border border-white/10 p-5 cursor-pointer hover:border-red-500/20 transition" onClick={() => onNavigate?.('withdrawal-requests')}>
          <h3 className="text-sm font-semibold text-gray-400 mb-4 flex items-center gap-2"><ArrowUpCircle className="w-4 h-4 text-red-400" /> Withdrawals</h3>
          <div className="grid grid-cols-2 gap-3">
            <MiniStat label="Total" value={`${fmtUSD(wi.total_amount)}`} sub={`${wi.total_count || 0} txns`} color="#ef4444" />
            <MiniStat label="Pending" value={s.pending_withdrawals || 0} color="#f97316" />
          </div>
        </div>
      </div>

      {/* Game Credits */}
      <div className="bg-[#131822] rounded-2xl border border-white/10 p-5">
        <h3 className="text-sm font-semibold text-gray-400 mb-4 flex items-center gap-2"><Gamepad2 className="w-4 h-4 text-[#D4AF37]" /> Game Credits</h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <MiniCard label="Total Loads" value={g.loads_count || 0} color="#6C5CE7" icon={ArrowDownCircle} />
          <MiniCard label="Load Volume" value={`${fmtUSD(g.loads_total)}`} color="#6C5CE7" icon={DollarSign} />
          <MiniCard label="Total Redeems" value={g.redeems_count || 0} color="#22c55e" icon={ArrowUpCircle} />
          <MiniCard label="Redeem Volume" value={`${fmtUSD(g.redeems_total)}`} color="#22c55e" icon={DollarSign} />
        </div>
      </div>

      {/* Recent Transactions */}
      <div className="bg-[#131822] rounded-2xl border border-white/10 p-5">
        <h3 className="text-sm font-semibold text-gray-400 mb-4 flex items-center gap-2 cursor-pointer" onClick={() => onNavigate?.('transactions')}>
          <Clock className="w-4 h-4 text-[#D4AF37]" /> Recent Transactions
          <span className="text-[10px] text-[#D4AF37] ml-auto">View All →</span>
        </h3>
        {(data.recent_transactions || []).length === 0 ? (
          <p className="text-center text-gray-500 py-6 text-sm">No transactions yet</p>
        ) : (
          <div className="space-y-2">
            {(data.recent_transactions || []).map((txn, i) => (
              <div key={txn.id || i} className="flex items-center justify-between p-3 rounded-lg bg-[#0B0E14]">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center"
                    style={{ background: txn.type === 'deposit' ? 'rgba(34,197,94,0.15)' : 'rgba(239,68,68,0.15)' }}>
                    {txn.type === 'deposit' ? <ArrowDownCircle className="w-4 h-4 text-green-400" /> : <ArrowUpCircle className="w-4 h-4 text-red-400" />}
                  </div>
                  <div>
                    <p className="text-sm font-medium text-white">{txn.username || 'Unknown'}</p>
                    <p className="text-xs text-gray-500 capitalize">{txn.type}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-sm font-semibold" style={{ color: txn.type === 'deposit' ? '#22c55e' : '#ef4444' }}>{fmtUSD(txn.amount)}</p>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${
                    txn.status === 'approved' || txn.status === 'completed' ? 'bg-green-500/20 text-green-400' :
                    txn.status === 'UNDER_REVIEW' || txn.status === 'PENDING_REVIEW' ? 'bg-yellow-500/20 text-yellow-400' :
                    'bg-gray-500/20 text-gray-400'
                  }`}>{txn.status}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function KPI({ icon: Icon, label, value, sub, color, urgent, onClick }) {
  return (
    <div className={`bg-[#131822] rounded-2xl border p-4 ${urgent ? 'border-yellow-500/30' : 'border-white/10'} ${onClick ? 'cursor-pointer hover:border-white/20 transition' : ''}`} onClick={onClick}>
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ background: `${color}15` }}>
          <Icon className="w-5 h-5" style={{ color }} />
        </div>
        <div>
          <p className="text-[11px] text-gray-500">{label}</p>
          <p className="text-xl font-bold text-white">{value}</p>
          {sub && <p className="text-[10px] text-gray-500">{sub}</p>}
        </div>
      </div>
    </div>
  );
}

function WalletRow({ label, value, color, bold }) {
  return (
    <div className="flex items-center justify-between">
      <span className={`text-xs ${bold ? 'text-white font-semibold' : 'text-gray-400'}`}>{label}</span>
      <span className={`text-sm font-mono ${bold ? 'font-bold' : 'font-medium'}`} style={{ color }}>{value}</span>
    </div>
  );
}

function MiniStat({ label, value, sub, color }) {
  return (
    <div>
      <p className="text-[10px] text-gray-500 mb-1">{label}</p>
      <p className="text-lg font-bold" style={{ color }}>{value}</p>
      {sub && <p className="text-[10px] text-gray-500">{sub}</p>}
    </div>
  );
}

function MiniCard({ label, value, icon: Icon, color }) {
  return (
    <div className="rounded-xl p-3" style={{ background: `${color}08`, border: `1px solid ${color}15` }}>
      <div className="flex items-center gap-2 mb-1.5">
        <Icon className="w-3.5 h-3.5" style={{ color }} />
        <p className="text-[10px] text-gray-500">{label}</p>
      </div>
      <p className="text-base font-bold" style={{ color }}>{value}</p>
    </div>
  );
}
